import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { getNotesAsync, setCurrentNote, updateNotesAsync } from '../../../../redux/flows/portal/notes'
import type { INotes } from '../../../../redux/flows/portal/notes'
import { useAppDispatch, type RootState } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'

/**
 * Simplified NotesList hook
 *
 * Responsibilities:
 * 1. Display list of pending notes
 * 2. Handle note selection
 * 3. Fetch patient history when note is selected
 * 4. Handle note approval
 */

export const useNotesList = () => {
  const dispatch = useAppDispatch()

  // ===== REDUX STATE =====
  const pendingNotes = useSelector((state: RootState) => state.notesPortal.notes)
  // Use selectedPendingNoteId so the highlight stays when viewing history notes
  const selectedPendingNoteId = useSelector((state: RootState) => state.notesPortal.selectedPendingNoteId)
  const currentNote = useSelector((state: RootState) => state.notesPortal.currentNote)
  const currentPatientId = useSelector((state: RootState) => state.notesPortal.currentPatientId)
  const patientNotes = useSelector((state: RootState) => state.notesPortal.patientNotes)
  const currentNoteUpdateStatus = useSelector((state: RootState) => state.notesPortal.currentNoteUpdateStatus)
  const localNoteValues = useSelector((state: RootState) => state.notesPortal.localNoteValues)

  // ===== HANDLERS =====

  /**
   * Handle note selection from the list
   * 1. Set the selected note in Redux
   * 2. Fetch patient notes history for that patient
   */
  const handleNoteSelect = (note: INotes) => {
    // Set selected note in Redux
    dispatch(setCurrentNote(note))

    // Fetch patient notes history if patient ID exists
    if (note.patient?.id) {
      dispatch(getNotesAsync(note.patient.id))
    }
  }

  /**
   * Handle note approval
   * Updates note status to 'accepted' and removes from pending list
   */
  const handleApproveForNote = async (note: INotes) => {
    // Ensure the clicked pending note becomes the selected one
    dispatch(setCurrentNote(note))

    let patientHistory: any = []
    if (note.patient?.id) {
      await dispatch(getNotesAsync(note.patient.id))
        .unwrap()
        .then((res) => {
          patientHistory = res.notes
        })
    }

    // Determine the target pending note to approve (clicked item first)
    const selectedPendingNote = pendingNotes.find((n) => n.id === note.id) || note
    let targetNoteId: string | null = null

    if (selectedPendingNote) {
      const matchedNote = patientHistory.find(
        (n: any) => n.status === 'pending' && n.visitId === selectedPendingNote?.visitId,
      )
      if (matchedNote) {
        targetNoteId = matchedNote.id
      }
    }
    const latestLocalValues = localNoteValues[targetNoteId ?? '']
    let approveTarget: any = {
      id: targetNoteId ?? '',
      subjective: latestLocalValues?.subjective,
      objective: latestLocalValues?.objective,
      assessment: latestLocalValues?.assessment,
      plan: latestLocalValues?.plan,
    }

    if (!approveTarget) return

    dispatch(
      updateNotesAsync({
        updatedNotes: {
          ...approveTarget,
          status: 'accepted',
        },
        notes: pendingNotes,
      }),
    )
  }

  // ===== RETURN VALUES =====

  return {
    // Data
    pendingNotes,
    selectedPendingNoteId,

    // Handlers
    handleNoteSelect,
    handleApproveForNote,

    // Status
    isApproving: currentNoteUpdateStatus === APIStatus.pending,
  }
}
